import { FC, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import $api from "../../../shared/api/axios";
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { formSchema } from "../model/types";
import { ZodError } from "zod";
import { useAuthenticated } from "../../../shared/hooks/useAuthenticated";

const selectItemClasses = "px-3 py-2 text-[14px] hover:bg-gray-50 cursor-pointer focus:bg-gray-50 focus:outline-none";

export const InviteUserPage: FC = () => {
  const { user } = useAuthenticated();
  const [email, setEmail] = useState("");
  const [orgId, setOrgId] = useState("");
  const [role, setRole] = useState("");
  const [accessMode, setAccessMode] = useState("edit");
  const [orgSearch, setOrgSearch] = useState("");
  const [errors, setErrors] = useState<{ email?: string; role?: string }>({});

  const { data: organizationsData } = useQuery<{ id: number; name: string }[]>({
    queryKey: ["get-organizations"],
    queryFn: async () => {
      const response = await $api.get("/organizations");
      return response?.data?.data || [];
    }
  });

  const filteredOrganizations =
    organizationsData?.filter((org) => org.name.toLowerCase().includes(orgSearch.toLowerCase())) || [];

  const inviteUserMutation = useMutation({
    mutationKey: ["generate-invite"],
    mutationFn: async () => {
      // Validate inputs
      try {
        formSchema.parse({ email, orgId, role });
      } catch (error) {
        if (error instanceof ZodError) {
          const fieldErrors: { email?: string; role?: string } = {};
          error.errors.forEach((err) => {
            if (err.path[0] === "email") fieldErrors.email = err.message;
            if (err.path[0] === "role") fieldErrors.role = err.message;
          });
          setErrors(fieldErrors);
          throw new Error("Validation failed");
        }
      }

      const response = await $api.get("/auth/generate-invite", {
        params: {
          email,
          orgId: orgId || undefined,
          role,
          accessMode
        }
      });
      return response.data;
    },
    onError: (e) => {
      if (e instanceof Error && e.message === "Validation failed") {
        return;
      }
      const msg = (e as AxiosError<{ message: string }>).response?.data?.message || "Error inviting user";
      alert(msg);
    },
    onSuccess: (data) => {
      alert(`Invite generated successfully. Invite URL: ${data.data?.url}`);
      setEmail("");
      setOrgId("");
      setRole("");
      setAccessMode("edit");
      setErrors({});
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    inviteUserMutation.mutate();
  };

  const selectedOrg = organizationsData?.find((org) => org.id.toString() === orgId);

  return (
    <div className="container mx-auto py-8 h-[calc(100vh-51px-64px)]">
      <div className="max-w-xl mx-auto">
        <div className="flex justify-between items-center mb-6 pr-6">
          <FigmaTypography component="h1" variant="memo-heading-1">Invite User</FigmaTypography>
        </div>
        <div className="mb-6">
          <p className="text-base font-medium">Invite a new user to the platform</p>
          <p className="text-[14px] text-gray-500">Fill in the details below to send an invitation email to the user.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[14px] font-medium">
              Email
            </Label>
            <Input
              id="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 rounded-md border border-gray-300 focus:border-gray-400 focus:ring-0"
            />
            {errors.email && <p className="text-red-500 text-[14px]">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="orgId" className="text-[14px] font-medium">
              Organization (Optional)
            </Label>
            <Select value={orgId} onValueChange={setOrgId}>
              <SelectTrigger
                id="orgId"
                className="h-11 rounded-md border border-gray-300 focus:border-gray-400 focus:ring-0"
              >
                <SelectValue placeholder="Select an organization">
                  {selectedOrg ? selectedOrg.name : "Select an organization"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                <div className="p-2 border-b border-gray-200">
                  <Input
                    placeholder="Search organizations..."
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    className="h-8 text-[14px] border-gray-300 focus:border-gray-400 focus:ring-0"
                  />
                </div>
                {filteredOrganizations.length === 0 ? (
                  <div className="px-3 py-2 text-[14px] text-gray-500">No organizations found</div>
                ) : (
                  filteredOrganizations.map((org) => (
                    <SelectItem key={org.id} value={org.id.toString()} className={selectItemClasses}>
                      {org.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role" className="text-[14px] font-medium">
              Role
            </Label>
            <Select
              value={role}
              onValueChange={(value) => {
                setRole(value);
                if (value === "systemAdmin") setAccessMode("edit");
              }}
            >
              <SelectTrigger
                id="role"
                className="h-11 rounded-md border border-gray-300 focus:border-gray-400 focus:ring-0"
              >
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 rounded-md shadow-lg">
                <SelectItem value="clientUser" className={selectItemClasses}>
                  Client User
                </SelectItem>
                <SelectItem value="clientAdmin" className={selectItemClasses}>
                  Client Admin
                </SelectItem>
                {user?.role === "systemAdmin" && (
                  <SelectItem value="systemAdmin" className={selectItemClasses}>
                    System Admin
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {errors.role && <p className="text-red-500 text-[14px]">{errors.role}</p>}
          </div>

          {role !== "systemAdmin" && (
            <div className="space-y-2">
              <Label htmlFor="accessMode" className="text-[14px] font-medium">
                Access Mode
              </Label>
              <Select value={accessMode} onValueChange={setAccessMode}>
                <SelectTrigger
                  id="accessMode"
                  className="h-11 rounded-md border border-gray-300 focus:border-gray-400 focus:ring-0"
                >
                  <SelectValue placeholder="Select access mode" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 rounded-md shadow-lg">
                  <SelectItem value="edit" className={selectItemClasses}>
                    Edit
                  </SelectItem>
                  <SelectItem value="read" className={selectItemClasses}>
                    Read
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <AppButton type="submit" disabled={inviteUserMutation.isPending} className="mt-2">
            {inviteUserMutation.isPending ? "Sending Invite..." : "Send Invite"}
          </AppButton>
        </form>
      </div>
    </div>
  );
};
